import { useState, useRef, useCallback, useEffect } from 'react';
import { formatDate, todayInputValue } from '@/utils/format';
import { showToast } from '@/components/base/Toast';
import { useModalBackHandler } from '@/hooks/useModalBackHandler';
import { confirmModal } from '@/components/base/ConfirmModal';
import { parseMaterialsNote } from '@/utils/logParser';
import { uploadFile, getFileDownloadUrl, uploadFailureMessage } from '@/api/file';
import { MediaCaptureSheet, type CapturedMedia } from '@/components/feature/MediaCaptureSheet';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import type { Diary, DiaryAttachment, Field, Season, MaterialSuggestion, Material, DiaryMaterial, DiaryIssueSeverity } from '@/types';
import { getMaterialsByFarmer, addMaterial, addEditHistory } from '@/store';
import { ScheduleStepPicker } from './ScheduleStepPicker';
import { useCategories } from '@/hooks/useCategories';
import { UNIT_OF_MEASURE_FALLBACK } from '@/constants/categoryFallbacks';

interface LogFormModalProps {
  editItem?: Diary | null;
  fields: Field[];
  seasons: Season[];
  farmerId: string;
  farmerMaterials: Material[];
  onSave: (data: Omit<Diary, 'id'>) => void;
  onUpdate?: (id: string, data: Partial<Diary>, materialChanges?: { itemId?: string; tenVatTu: string; nhomVatTu?: string; soLuongGiam: number; donVi: string }[]) => void;
  onClose: () => void;
}

export function LogFormModal({ editItem, fields, seasons, farmerId, farmerMaterials, onSave, onUpdate, onClose }: LogFormModalProps) {

  const [fieldId, setFieldId] = useState(editItem?.fieldId || fields[0]?.id || '');
  const [seasonId, setSeasonId] = useState(editItem?.seasonId || '');
  const [scheduleEventId, setScheduleEventId] = useState(editItem?.scheduleEventId || '');
  const [ngay, setNgay] = useState(editItem?.ngay || todayInputValue());
  const [hoatDong, setHoatDong] = useState(editItem?.hoatDong || '');
  const [chiTiet, setChiTiet] = useState(editItem?.chiTiet || '');
  const [materialsNote, setMaterialsNote] = useState(editItem?.materialsNote || '');
  const [materials, setMaterials] = useState<DiaryMaterial[]>(editItem?.materials || []);
  const [laborHours, setLaborHours] = useState(editItem?.laborHours?.toString() || '');
  const [issueNote, setIssueNote] = useState(editItem?.issueNote || '');
  const [issueSeverity, setIssueSeverity] = useState<DiaryIssueSeverity | ''>(editItem?.issueSeverity || '');
  const [ghiChuKhac, setGhiChuKhac] = useState(editItem?.ghiChuKhac || '');
  const [attachments, setAttachments] = useState<DiaryAttachment[]>(editItem?.attachments || []);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [uploadingCount, setUploadingCount] = useState(0);
  const [captureOpen, setCaptureOpen] = useState(false);

  const [materialSuggestions, setMaterialSuggestions] = useState<MaterialSuggestion[]>(editItem?.materialSuggestions || []);
  const [aiSummary, setAiSummary] = useState('');
  const [showSuggestionBox, setShowSuggestionBox] = useState(false);

  const filteredSeasons = seasons.filter((s) => s.fieldId === fieldId);
  const { names: unitOptions } = useCategories('UNIT_OF_MEASURE', UNIT_OF_MEASURE_FALLBACK);

  const requestClose = useCallback(async () => {
    const dirty =
      hoatDong.trim() !== (editItem?.hoatDong || '') ||
      chiTiet.trim() !== (editItem?.chiTiet || '') ||
      materialsNote.trim() !== (editItem?.materialsNote || '') ||
      issueNote.trim() !== (editItem?.issueNote || '') ||
      ghiChuKhac.trim() !== (editItem?.ghiChuKhac || '') ||
      attachments.length !== (editItem?.attachments?.length || 0);
    if (!dirty) {
      onClose();
      return;
    }
    const ok = await confirmModal({
      title: 'Hủy nhật ký này?',
      message: 'Dữ liệu, ảnh hoặc ghi âm bạn vừa nhập sẽ bị mất nếu thoát ra bây giờ.',
      confirmText: 'Thoát, không lưu',
      cancelText: 'Tiếp tục nhập',
      variant: 'danger',
    });
    if (ok) onClose();
  }, [hoatDong, chiTiet, materialsNote, issueNote, ghiChuKhac, attachments.length, editItem, onClose]);

  useModalBackHandler(true, requestClose);

  useEffect(() => {
    if (editItem) {
      setFieldId(editItem.fieldId || fields[0]?.id || '');
      setSeasonId(editItem.seasonId || '');
      setScheduleEventId(editItem.scheduleEventId || '');
      setNgay(editItem.ngay || todayInputValue());
      setHoatDong(editItem.hoatDong || '');
      setChiTiet(editItem.chiTiet || '');
      setMaterialsNote(editItem.materialsNote || '');
      setMaterials(editItem.materials || []);
      setLaborHours(editItem.laborHours?.toString() || '');
      setIssueNote(editItem.issueNote || '');
      setIssueSeverity(editItem.issueSeverity || '');
      setGhiChuKhac(editItem.ghiChuKhac || '');
      setAttachments(editItem.attachments || []);
      setMaterialSuggestions(editItem.materialSuggestions || []);
    }
  }, [editItem, fields]);

  const handleFieldChange = (fid: string) => {
    setFieldId(fid);
    const nextSeason = seasons.find((season) => season.fieldId === fid && ['Đang canh tác', 'Chuẩn bị'].includes(season.status)) || seasons.find((season) => season.fieldId === fid);
    setSeasonId(nextSeason?.id || '');
    setScheduleEventId('');
  };

  const analyzeMaterials = useCallback((note: string) => {
    if (note.trim().length < 3) {
      setShowSuggestionBox(false);
      setMaterialSuggestions([]);
      setAiSummary('');
      return;
    }
    const currentSeasonId = seasonId || undefined;
    const { suggestions, summary } = parseMaterialsNote(note, currentSeasonId);
    if (suggestions.length > 0) {
      setMaterialSuggestions(suggestions);
      setAiSummary(summary);
      setShowSuggestionBox(true);
    } else {
      setShowSuggestionBox(false);
      setMaterialSuggestions([]);
      setAiSummary('');
    }
  }, [seasonId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (materialsNote.trim()) {
        analyzeMaterials(materialsNote);
      } else {
        setShowSuggestionBox(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [materialsNote, analyzeMaterials]);

  const applyMaterialSuggestion = (idx: number) => {
    const s = materialSuggestions[idx];
    if (s.applied) return;

    const existingMat = farmerMaterials.find(
      (m) => m.tenVatTu.toLowerCase().includes(s.tenVatTu.toLowerCase()) || s.tenVatTu.toLowerCase().includes(m.tenVatTu.toLowerCase())
    );

    if (existingMat) {
      // The real backend has no route to adjust an existing Material's
      // quantity (only a status transition) — can't auto-decrement stock here.
      showToast(`"${existingMat.tenVatTu}" đã có trong kho — vui lòng cập nhật số lượng thủ công tại mục Vật tư`, 'info');
    } else {
      addMaterial({
        farmerId,
        seasonId: s.seasonId,
        tenVatTu: s.tenVatTu,
        nhomVatTu: s.nhomVatTu,
        soLuong: s.soLuong,
        tonKho: s.soLuong,
        donVi: s.donVi,
        nguonGoc: 'Nông hộ tự mua',
        ngayCap: ngay,
        status: 'Đủ',
        ghiChu: `Tạo từ nhật ký ngày ${ngay}`,
      });
      showToast(`Đã tạo vật tư mới "${s.tenVatTu}" (${s.soLuong}${s.donVi})`, 'success');
    }

    const updated = [...materialSuggestions];
    updated[idx] = { ...updated[idx], applied: true };
    setMaterialSuggestions(updated);
  };

  const dismissSuggestion = (idx: number) => {
    const updated = [...materialSuggestions];
    updated[idx] = { ...updated[idx], applied: true };
    setMaterialSuggestions(updated);
    if (updated.every((s) => s.applied)) setShowSuggestionBox(false);
  };

  const dismissAllSuggestions = () => {
    setShowSuggestionBox(false);
    setMaterialSuggestions(materialSuggestions.map((s) => ({ ...s, applied: true })));
  };

  const handleFilesSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    e.target.value = '';
    if (!files || files.length === 0) return;
    setUploadingCount((n) => n + files.length);
    for (const file of Array.from(files)) {
      try {
        const fileId = await uploadFile(file, 'FARM_LOG_ATTACHMENT', file.type);
        setAttachments((prev) => [...prev, { fileId, type: file.type.startsWith('video') ? 'video' : 'image', caption: file.name }]);
      } catch (err) {
        showToast(uploadFailureMessage(err, `"${file.name}"`), 'error');
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
  }, []);

  /** Uploads a blob captured in-app; same presign path as a picked file. */
  const handleCaptured = useCallback(async (media: CapturedMedia) => {
    setUploadingCount((n) => n + 1);
    try {
      const ext = media.kind === 'image' ? 'jpg' : 'webm';
      const fileId = await uploadFile(media.blob, 'FARM_LOG_ATTACHMENT', media.blob.type);
      setAttachments((prev) => [
        ...prev,
        { fileId, type: media.kind, caption: `${media.kind === 'image' ? 'Ảnh' : 'Video'} hiện trường.${ext}` },
      ]);
    } catch (err) {
      showToast(uploadFailureMessage(err, media.kind === 'image' ? 'ảnh' : 'video'), 'error');
    } finally {
      setUploadingCount((n) => n - 1);
    }
  }, []);

  const audio = useAudioRecorder(
    useCallback(async (blob: Blob) => {
      setUploadingCount((n) => n + 1);
      try {
        const fileId = await uploadFile(blob, 'FARM_LOG_ATTACHMENT', blob.type);
        setAttachments((prev) => [...prev, { fileId, type: 'audio', caption: 'Ghi âm hiện trường' }]);
      } catch (err) {
        showToast(uploadFailureMessage(err, 'bản ghi âm'), 'error');
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }, []),
    useCallback((message: string) => showToast(message, 'warning'), []),
  );

  useEffect(() => {
    let cancelled = false;
    attachments.forEach((a) => {
      if (attachmentUrls[a.fileId]) return;
      getFileDownloadUrl(a.fileId)
        .then((url) => {
          if (!cancelled) setAttachmentUrls((prev) => ({ ...prev, [a.fileId]: url }));
        })
        .catch(() => {
          // Thumbnail preview only — leave the icon fallback if it fails to resolve.
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  const removeAttachment = (fileId: string) => setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));

  const addMaterialRow = () => setMaterials((prev) => [...prev, { name: '', quantity: 0, unit: '' }]);
  const updateMaterialRow = (idx: number, patch: Partial<DiaryMaterial>) => {
    setMaterials((prev) => prev.map((m, i) => {
      if (i !== idx) return m;
      const next = { ...m, ...patch };
      next.amountVnd = next.unitCostVnd ? next.quantity * next.unitCostVnd : undefined;
      return next;
    }));
  };
  const removeMaterialRow = (idx: number) => setMaterials((prev) => prev.filter((_, i) => i !== idx));
  const totalMaterialCost = materials.reduce((sum, m) => sum + (m.amountVnd ?? 0), 0);

  const handleSubmit = () => {
    if (!fieldId) { showToast('Vui lòng chọn thửa đất', 'warning'); return; }
    if (!ngay) { showToast('Vui lòng chọn ngày ghi nhật ký', 'warning'); return; }
    if (!hoatDong.trim()) { showToast('Vui lòng nhập tiêu đề nhật ký', 'warning'); return; }
    if (!chiTiet.trim()) { showToast('Vui lòng nhập nội dung nhật ký', 'warning'); return; }
    if (uploadingCount > 0) { showToast('Đang tải lên tệp đính kèm, vui lòng đợi', 'warning'); return; }

    const activeSuggestions = materialSuggestions.filter((s) => !s.applied);

    const payload: Partial<Diary> = {
      fieldId,
      seasonId: seasonId || undefined,
      scheduleEventId: scheduleEventId || undefined,
      ngay,
      hoatDong: hoatDong.trim(),
      chiTiet: chiTiet.trim(),
      materialsNote: materialsNote.trim() || undefined,
      materialSuggestions: activeSuggestions.length > 0 ? activeSuggestions : undefined,
      materials: materials.filter((m) => m.name.trim() && m.quantity > 0).length > 0 ? materials.filter((m) => m.name.trim() && m.quantity > 0) : undefined,
      laborHours: laborHours ? Number(laborHours) : undefined,
      issueNote: issueNote.trim() || undefined,
      issueSeverity: issueSeverity || undefined,
      ghiChuKhac: ghiChuKhac.trim() || undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    if (editItem && onUpdate) {
      onUpdate(editItem.id, payload);
    } else {
      onSave(payload as Omit<Diary, 'id'>);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 animate-modal-backdrop" onClick={requestClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">
            {editItem ? 'Sửa nhật ký' : 'Tạo nhật ký mới'}
          </h3>
          <button type="button" onClick={requestClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Field - season is inferred from active crop cycle */}
          <div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Thửa đất <span className="text-red-500">*</span></label>
              <select
                value={fieldId}
                onChange={(e) => handleFieldChange(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              >
                <option value="">Chọn thửa đất</option>
                {fields.map((f) => (
                  <option key={f.id} value={f.id}>{f.maThuaDat} - {f.name}</option>
                ))}
              </select>
            </div>
            {fieldId && (
              <p className="mt-1 text-xs text-slate-400">
                Mùa vụ tự gắn: {filteredSeasons.find((season) => season.id === seasonId)?.name || 'chưa có mùa vụ đang canh tác'}
              </p>
            )}
          </div>

          {/* Which "bước" (step) of the season's cultivation process, if any was applied — renders nothing for a standalone/process-free season */}
          <ScheduleStepPicker seasonId={seasonId || undefined} value={scheduleEventId} onChange={setScheduleEventId} />

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ngày ghi nhật ký <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={ngay}
              onChange={(e) => setNgay(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            />
          </div>

          {/* Content + Voice recording */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tiêu đề <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={hoatDong}
              onChange={(e) => setHoatDong(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="VD: Kiểm tra sâu bệnh sau mưa"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Chi tiết kiểm tra <span className="text-red-500">*</span></label>
            <textarea
              value={chiTiet}
              onChange={(e) => setChiTiet(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 min-h-[100px] resize-none"
              placeholder="Mô tả chi tiết hoạt động sản xuất hôm nay..."
            />
          </div>

          {/* Materials note + AI suggestion */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú vật tư sử dụng</label>
            <input
              type="text"
              value={materialsNote}
              onChange={(e) => setMaterialsNote(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="VD: Dùng NPK 50kg, thuốc BVTV 2 lít..."
            />

            {showSuggestionBox && materialSuggestions.length > 0 && (
              <div className="mt-3 bg-gradient-to-r from-agrida-50 to-agrida-50 border border-agrida-200 rounded-xl p-3 animate-slide-down">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center text-agrida-600"><i className="ri-robot-line" /></span>
                    <span className="text-xs font-semibold text-agrida-800">AI gợi ý vật tư</span>
                  </div>
                  <button type="button" onClick={dismissAllSuggestions} className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap">Bỏ qua tất cả</button>
                </div>

                {aiSummary && (
                  <p className="text-xs text-slate-600 mb-2">{aiSummary}</p>
                )}

                <div className="space-y-2">
                  {materialSuggestions.filter((s) => !s.applied).map((s, idx) => {
                    const realIdx = materialSuggestions.indexOf(s);
                    return (
                      <div key={realIdx} className="bg-white rounded-xl border border-slate-100 p-2.5 flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">{s.tenVatTu}</span>
                            {s.nhomVatTu && (
                              <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{s.nhomVatTu}</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {s.soLuong} {s.donVi}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => applyMaterialSuggestion(realIdx)}
                            className="px-2.5 py-1.5 rounded-xl text-[10px] font-bold bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap"
                          >
                            Cập nhật vào vật tư
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissSuggestion(realIdx)}
                            className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-slate-500"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Structured materials/cost breakdown + labor hours + issue tracking */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">Vật tư & chi phí (theo dòng)</label>
              <button type="button" onClick={addMaterialRow} className="text-xs font-medium text-agrida-700 hover:underline whitespace-nowrap">+ Thêm dòng</button>
            </div>
            <div className="space-y-2">
              {materials.map((m, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={m.name}
                    onChange={(e) => updateMaterialRow(idx, { name: e.target.value })}
                    placeholder="Tên vật tư"
                    className="flex-1 min-w-0 px-2 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  />
                  <input
                    type="number"
                    value={m.quantity || ''}
                    onChange={(e) => updateMaterialRow(idx, { quantity: Number(e.target.value) })}
                    placeholder="SL"
                    className="w-14 px-2 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  />
                  <input
                    type="text"
                    list="log-material-unit-options"
                    value={m.unit}
                    onChange={(e) => updateMaterialRow(idx, { unit: e.target.value })}
                    placeholder="ĐVT"
                    className="w-14 px-2 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  />
                  <input
                    type="number"
                    value={m.unitCostVnd || ''}
                    onChange={(e) => updateMaterialRow(idx, { unitCostVnd: Number(e.target.value) })}
                    placeholder="Đơn giá"
                    className="w-20 px-2 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  />
                  <button type="button" onClick={() => removeMaterialRow(idx)} className="w-6 h-6 shrink-0 flex items-center justify-center text-slate-300 hover:text-red-500">
                    <i className="ri-close-line" />
                  </button>
                </div>
              ))}
              {materials.length === 0 && <p className="text-xs text-slate-400">Chưa có dòng vật tư nào</p>}
              <datalist id="log-material-unit-options">
                {unitOptions.map((u) => (<option key={u} value={u} />))}
              </datalist>
              {totalMaterialCost > 0 && (
                <p className="text-xs font-medium text-slate-600 text-right">Tổng chi phí: {totalMaterialCost.toLocaleString('vi-VN')} đ</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Giờ công</label>
              <input
                type="number"
                value={laborHours}
                onChange={(e) => setLaborHours(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder="VD: 4"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mức độ vấn đề</label>
              <select
                value={issueSeverity}
                onChange={(e) => setIssueSeverity(e.target.value as DiaryIssueSeverity | '')}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              >
                <option value="">Không có vấn đề</option>
                <option value="LOW">Nhẹ</option>
                <option value="MEDIUM">Trung bình</option>
                <option value="HIGH">Nghiêm trọng</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Vấn đề cụ thể gặp phải</label>
            <textarea
              value={issueNote}
              onChange={(e) => setIssueNote(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 min-h-[70px] resize-none"
              placeholder="VD: Sâu bệnh xuất hiện nhiều hơn dự kiến, thời tiết bất thường..."
            />
          </div>

          {/* Ghi chú khác */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú khác</label>
            <input
              type="text"
              value={ghiChuKhac}
              onChange={(e) => setGhiChuKhac(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="Ghi chú thêm..."
            />
          </div>

          {/* Photo / Video / Voice attachments — real capture, uploaded via the file-svc presign flow */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Đính kèm (ảnh / video / ghi âm)</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {/* Live capture. The picker below stays for anything already on the
                  phone — `capture` on an <input> is only a hint a mobile browser may
                  honour, and does nothing on desktop, so it was never a way to
                  actually take a photo or record a clip from inside the app. */}
              <button
                type="button"
                onClick={() => setCaptureOpen(true)}
                data-testid="open-capture"
                className="px-3 py-2 rounded-xl bg-agrida-600 text-white text-xs font-medium flex items-center gap-1 whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-camera-line" /></span>
                Chụp / Quay
              </button>
              <label className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-1 whitespace-nowrap cursor-pointer">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-image-add-line" /></span>
                Chọn từ thư viện
                <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFilesSelected} data-testid="pick-media" />
              </label>
              <button
                type="button"
                onClick={audio.toggle}
                aria-pressed={audio.recording}
                className={`px-3 py-2 rounded-xl border text-xs font-medium flex items-center gap-1 whitespace-nowrap ${audio.recording ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={audio.recording ? 'ri-stop-circle-fill' : 'ri-mic-line'} /></span>
                {audio.state === 'starting'
                  ? 'Đang mở micro...'
                  : audio.recording
                    ? `Dừng ghi ${Math.floor(audio.elapsed / 60)}:${String(audio.elapsed % 60).padStart(2, '0')}`
                    : 'Ghi âm'}
              </button>
              {uploadingCount > 0 && <span className="text-xs text-slate-400 self-center">Đang tải {uploadingCount}...</span>}
            </div>

            {/* Review what was just attached. This used to be a 32px thumbnail per
                row and, for audio, only a mic icon — so a farmer could not check the
                photo they had taken and could not play back a recording at all. */}
            {attachments.length > 0 && (
              <ul className="space-y-2 mb-2">
                {attachments.map((a) => {
                  const url = attachmentUrls[a.fileId];
                  return (
                    <li key={a.fileId} className="rounded-xl bg-slate-50 p-2" data-testid={`attachment-${a.type}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 truncate text-xs text-slate-600">{a.caption ?? a.fileId}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(a.fileId)}
                          aria-label="Xoá đính kèm"
                          className="shrink-0 text-slate-300 hover:text-red-500"
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>

                      {!url ? (
                        <p className="mt-1 text-[11px] text-slate-400">Đang tải bản xem trước...</p>
                      ) : a.type === 'audio' ? (
                        <audio src={url} controls preload="metadata" data-testid="attachment-audio-player" className="mt-1.5 w-full" />
                      ) : a.type === 'video' ? (
                        <video src={url} controls preload="metadata" data-testid="attachment-video-player" className="mt-1.5 w-full max-h-56 rounded-lg bg-black" />
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" title="Mở ảnh kích thước đầy đủ">
                          <img
                            src={url}
                            alt={a.caption ?? 'ảnh đính kèm'}
                            data-testid="attachment-image-preview"
                            className="mt-1.5 w-full max-h-56 rounded-lg object-contain bg-black"
                          />
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <MediaCaptureSheet open={captureOpen} onClose={() => setCaptureOpen(false)} onCaptured={handleCaptured} />

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex gap-3 justify-end">
          <button
            type="button"
            onClick={requestClose}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors whitespace-nowrap min-h-[44px]"
          >
            {editItem ? 'Lưu thay đổi' : 'Tạo nhật ký'}
          </button>
        </div>
      </div>
    </div>
  );
}
